import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "title" }
const _hoisted_2 = { class: "title_text" }
const _hoisted_3 = { class: "event-card__date secondary_text" }
const _hoisted_4 = { class: "event-card__room secondary_text" }
const _hoisted_5 = { class: "event-card__description" }
const _hoisted_6 = ["href", "title"]
const _hoisted_7 = ["alt", "src"]
const _hoisted_8 = {
  key: 0,
  class: "secondary_text"
}
const _hoisted_9 = { class: "event-card__invitation-info" }
const _hoisted_10 = {
  key: 0,
  class: "secondary_text"
}
const _hoisted_11 = { class: "event-card__invitation-info hovered" }

import type { Conversation, DashboardEventRoom } from '../../types/index.ts'

import { getCanonicalLocale, getLanguage, n, t } from '@nextcloud/l10n'
import { imagePath } from '@nextcloud/router'
import { usernameToColor } from '@nextcloud/vue/functions/usernameToColor'
import { useNow } from '@vueuse/core'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import NcButton from '@nextcloud/vue/components/NcButton'
import NcChip from '@nextcloud/vue/components/NcChip'
import NcIconSvgWrapper from '@nextcloud/vue/components/NcIconSvgWrapper'
import IconCalendarBlankOutline from 'vue-material-design-icons/CalendarBlankOutline.vue'
import IconVideo from 'vue-material-design-icons/Video.vue' // Filled for better indication
import IconVideoOutline from 'vue-material-design-icons/VideoOutline.vue'
import ConversationIcon from '../ConversationIcon.vue'
import IconTalk from '../../../img/app-dark.svg?raw'
import { useIsInCall } from '../../composables/useIsInCall.js'
import { CONVERSATION } from '../../constants.ts'
import { formattedTime, ONE_DAY_IN_MS } from '../../utils/formattedTime.ts'

type ConversationFromEvent = Pick<Conversation, 'token' | 'type' | 'name' | 'displayName' | 'avatarVersion' | 'callStartTime' | 'hasCall'>


export default /*@__PURE__*/_defineComponent({
  __name: 'EventCard',
  props: {
    eventRoom: {}
  },
  setup(__props: any) {


const props = __props
const store = useStore()
const router = useRouter()
const isInCall = useIsInCall()

const conversation = computed<ConversationFromEvent>(() => {
	return store.getters.conversation(props.eventRoom.roomToken) ?? {
		token: props.eventRoom.roomToken,
		type: props.eventRoom.roomType,
		name: props.eventRoom.roomName,
		displayName: props.eventRoom.roomDisplayName,
		avatarVersion: props.eventRoom.roomAvatarVersion,
		callStartTime: props.eventRoom.roomActiveSince ?? 0,
		hasCall: props.eventRoom.roomActiveSince !== null,
	}
})

const hasCall = computed(() => {
	return (conversation.value.hasCall || props.eventRoom.roomActiveSince !== null)
		&& props.eventRoom.start * 1000 >= (Date.now() - 600_000) // 10 minutes buffer
})

const elapsedTime = computed(() => {
	if (!hasCall.value || !(props.eventRoom.roomActiveSince ?? conversation.value.callStartTime)) {
		return ''
	}
	return formattedTime(+useNow({ interval: 1_000 }).value - (props.eventRoom.roomActiveSince ?? conversation.value.callStartTime) * 1000)
})

const isToday = computed(() => {
	return new Date(props.eventRoom.start * 1000).toDateString() === new Date().toDateString()
})

const eventDateLabel = computed(() => {
	if (hasCall.value) {
		return t('spreed', 'Ongoing')
	}
	const startDate = new Date(props.eventRoom.start * 1000)
	const endDate = new Date(props.eventRoom.end * 1000)
	const isTomorrow = startDate.toDateString() === new Date(Date.now() + ONE_DAY_IN_MS).toDateString()

	let time
	if (startDate.toDateString() === endDate.toDateString()) {
		if (isToday.value || isTomorrow) {
			// show the time only
			const timeRange = Intl.DateTimeFormat(getCanonicalLocale(), {
				hour: 'numeric',
				minute: 'numeric',
			}).formatRange(startDate, endDate)

			const relativeFormatter = new Intl.RelativeTimeFormat(getLanguage(), { numeric: 'auto' })

			// TRANSLATORS: e.g. "Tomorrow 10:00 - 11:00"
			time = t('spreed', '{dayPrefix} {dateTime}', {
				dayPrefix: isToday.value ? relativeFormatter.format(0, 'day') : relativeFormatter.format(1, 'day'),
				dateTime: timeRange,
			})
		} else {
			time = Intl.DateTimeFormat(getCanonicalLocale(), {
				weekday: 'long',
				hour: 'numeric',
				minute: 'numeric',
			}).formatRange(startDate, endDate)
		}
	} else {
		// show the month and the year as well
		time = Intl.DateTimeFormat(getCanonicalLocale(), {
			month: 'long',
			year: 'numeric',
			day: '2-digit',
			hour: 'numeric',
			minute: 'numeric',
		}).formatRange(startDate, endDate)
	}

	return time
})

const totalAttachments = computed(() => Object.values(props.eventRoom.eventAttachments))

const invitesLabel = computed(() => {
	const acceptedInvites = props.eventRoom.accepted ? n('spreed', '%n person accepted', '%n people accepted', props.eventRoom.accepted) : ''
	const declinedInvites = props.eventRoom.declined ? n('spreed', '%n person declined', '%n people declined', props.eventRoom.declined) : ''
	// FIXME should be a translated string ??
	return [acceptedInvites, declinedInvites].filter(Boolean).join(', ')
})

const attachmentInfo = computed(() => {
	if (!totalAttachments.value.length) {
		return null
	}
	const file = totalAttachments.value[0]

	return {
		icon: OC.MimeType.getIconUrl(file.fmttype) || imagePath('core', 'filetypes/file'),
		label: file.filename.replace(/^\//, ''),
		extraLabel: totalAttachments.value.length > 1
			? n('spreed', 'and %n other attachment', 'and %n other attachments', totalAttachments.value.length - 1)
			: '',
		url: file.previewLink ?? undefined,
	}
})

const roomLabel = computed(() => {
	return props.eventRoom.roomType === CONVERSATION.TYPE.ONE_TO_ONE
		? t('spreed', 'With {displayName}', { displayName: props.eventRoom.roomDisplayName }, { escape: false, sanitize: false })
		: t('spreed', 'In {conversation}', { conversation: props.eventRoom.roomDisplayName }, { escape: false, sanitize: false })
})

/**
 * Redirects to the conversation page and opens media settings
 *
 * @param data object
 * @param data.call - if true, opens the media settings
 */
function handleJoin({ call }: { call: boolean }) {
	router.push({
		name: 'conversation',
		params: { token: props.eventRoom.roomToken },
		hash: call ? '#direct-call' : undefined,
	})
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["event-card", {
			'event-card--highlighted': isToday.value,
			'event-card--in-call': hasCall.value,
		}])
  }, [
    _createElementVNode("h4", _hoisted_1, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.eventRoom.calendars, (calendar) => {
        return (_openBlock(), _createElementBlock("span", {
          key: calendar.principalUri,
          class: "calendar-badge",
          style: _normalizeStyle({ backgroundColor: calendar.calendarColor ?? _unref(usernameToColor)(calendar.principalUri).color })
        }, null, 4))
      }), 128)),
      _createElementVNode("span", _hoisted_2, _toDisplayString(props.eventRoom.eventName), 1)
    ]),
    _createElementVNode("p", _hoisted_3, [
      _createElementVNode("span", null, _toDisplayString(eventDateLabel.value), 1),
      (hasCall.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createVNode(IconVideo, {
              size: 20,
              "fill-color": "var(--color-border-error)"
            }),
            _createElementVNode("span", null, _toDisplayString(elapsedTime.value), 1)
          ], 64))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("span", _hoisted_4, [
      _createVNode(_unref(NcChip), {
        variant: "tertiary",
        text: roomLabel.value,
        "no-close": ""
      }, {
        icon: _withCtx(() => [
          _createVNode(ConversationIcon, {
            item: conversation.value,
            "hide-user-status": "",
            size: 20
          }, null, 8, ["item"])
        ]),
        _: 1
      }, 8, ["text"])
    ]),
    _createElementVNode("span", _hoisted_5, _toDisplayString(props.eventRoom.eventDescription), 1),
    (attachmentInfo.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createElementVNode("a", {
            class: "event-card__attachment",
            role: "link",
            href: attachmentInfo.value.url,
            title: _unref(t)('spreed', 'View attachment'),
            target: "_blank"
          }, [
            _createElementVNode("img", {
              class: "file-preview__image",
              alt: attachmentInfo.value.label,
              src: attachmentInfo.value.icon
            }, null, 8, _hoisted_7),
            _createElementVNode("span", null, _toDisplayString(attachmentInfo.value.label), 1)
          ], 8, _hoisted_6),
          (attachmentInfo.value.extraLabel)
            ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(attachmentInfo.value.extraLabel), 1))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true),
    _createElementVNode("span", _hoisted_9, [
      (invitesLabel.value && !hasCall.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(invitesLabel.value), 1))
        : _createCommentVNode("", true),
      ((hasCall.value && !_unref(isInCall)))
        ? (_openBlock(), _createBlock(_unref(NcButton), {
            key: 1,
            variant: "primary",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (handleJoin({ call: true })))
          }, {
            icon: _withCtx(() => [
              _createVNode(IconVideoOutline, { size: 20 })
            ]),
            default: _withCtx(() => [
              _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Join')), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("span", _hoisted_11, [
      _createVNode(_unref(NcButton), {
        variant: "tertiary",
        onClick: _cache[1] || (_cache[1] = ($event: any) => (handleJoin({ call: false })))
      }, {
        icon: _withCtx(() => [
          _createVNode(_unref(NcIconSvgWrapper), {
            svg: _unref(IconTalk),
            size: 20
          }, null, 8, ["svg"])
        ]),
        default: _withCtx(() => [
          _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'View conversation')), 1)
        ]),
        _: 1
      }),
      _createVNode(_unref(NcButton), {
        variant: "tertiary",
        href: props.eventRoom.eventLink,
        target: "_blank",
        title: _unref(t)('spreed', 'View event on Calendar'),
        "aria-label": _unref(t)('spreed', 'View event on Calendar')
      }, {
        icon: _withCtx(() => [
          _createVNode(IconCalendarBlankOutline, { size: 20 })
        ]),
        _: 1
      }, 8, ["href", "title", "aria-label"])
    ])
  ], 2))
}
}

})